import React, { useState, useRef } from 'react';
import { formatPrice, parseProductKey, sanitizeInput } from './AppUtils.js';
import { getMissingCompulsoryCategories } from './constants.js';

const validateEmail = (email) => {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(email) && email.length < 100;
};

const validatePhone = (phone) => {
  const re = /^[\d\s\-()+]+$/;
  return re.test(phone) && phone.length >= 7 && phone.length <= 20;
};

const validateName = (name) => {
  const re = /^[a-zA-Z\s\-']+$/;
  return re.test(name) && name.length >= 2 && name.length <= 100;
};

const EmailSalesForm = ({ selectedProducts, totalPrice, onClose, compulsoryData, productData }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    country: '',
    message: '',
    consent: false,
    marketingConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});
  
  // Rate limiting
  const lastSubmitTime = useRef(0);
  const submitAttempts = useRef(0);
  const RATE_LIMIT_DELAY = 5000;
  const MAX_ATTEMPTS_PER_SESSION = 5;

  // Generate HTML build summary for email
  const generateBuildSummaryHTML = () => {
    const productsByCategory = {};

    Object.entries(selectedProducts).forEach(([productKey, product]) => {
      const { mainCategory } = parseProductKey(productKey);
      if (!productsByCategory[mainCategory]) {
        productsByCategory[mainCategory] = [];
      }
      productsByCategory[mainCategory].push(product);
    });

    let html = '';

    Object.entries(productsByCategory).forEach(([category, products]) => {
      html += `<div style="margin-bottom: 15px;">`;
      html += `<h4 style="color: #ffd700; font-size: 12px; text-transform: uppercase; margin: 0 0 8px 0; font-weight: 600; letter-spacing: 0.5px;">${sanitizeInput(category)}</h4>`;

      products.forEach(product => {
        const safeName = sanitizeInput(product.name);
        const safePrice = formatPrice(product.price);
        html += `<p style="color: #cccccc; font-size: 14px; margin: 0 0 5px 0; padding-left: 10px;">• ${safeName} - <span style="color: #ffffff; font-weight: 600;">${safePrice}</span></p>`;
      });

      html += `</div>`;
    });

    return html;
  };

  // Generate plain text item list for sales email
  const generatePlainTextList = () => {
    return Object.values(selectedProducts).map(product => {
      const safeName = sanitizeInput(product.name);
      const safePrice = formatPrice(product.price);
      return `${safeName} - ${safePrice}`;
    }).join('\n');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setErrors(prev => ({ ...prev, [name]: '' }));
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      const sanitizedValue = name === 'message' 
        ? value.slice(0, 1000)
        : value.slice(0, 100);
      
      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (!validateName(formData.name)) {
      newErrors.name = 'Please enter a valid name (letters only)';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData.consent) {
      newErrors.consent = 'You must agree to the privacy policy to submit';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check for missing compulsory categories first
    const missingCompulsory = getMissingCompulsoryCategories(selectedProducts, compulsoryData, productData);
    if (missingCompulsory.length > 0) {
      const missingNames = missingCompulsory.map(item => item.name).join(', ');
      setErrors({ submit: `Please select products from these required categories: ${missingNames}` });
      return;
    }

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmitTime.current < RATE_LIMIT_DELAY) {
      const waitTime = Math.ceil((RATE_LIMIT_DELAY - (now - lastSubmitTime.current)) / 1000);
      setErrors({ submit: `Please wait ${waitTime} seconds before submitting again` });
      return;
    }

    if (submitAttempts.current >= MAX_ATTEMPTS_PER_SESSION) {
      setErrors({ submit: 'Maximum submission attempts reached. Please refresh the page.' });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    lastSubmitTime.current = now;
    submitAttempts.current += 1;

    try {
      const buildSummaryHTML = generateBuildSummaryHTML();
      const plainTextList = generatePlainTextList();
      const submissionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Prepare data for Netlify function
      const emailData = {
        customer_name: sanitizeInput(formData.name),
        customer_email: sanitizeInput(formData.email),
        customer_phone: sanitizeInput(formData.phone),
        customer_country: sanitizeInput(formData.country),
        customer_message: sanitizeInput(formData.message),
        build_summary: buildSummaryHTML,
        plain_text_list: plainTextList,
        total_price: formatPrice(totalPrice),
        submission_id: submissionId
      };

      // Call Netlify serverless function
      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailData)
      });

      if (response.ok || response.status === 202) {
        setSubmitStatus('success');
        
        // Clear form and close
        setTimeout(() => {
          setFormData({
            name: '',
            phone: '',
            email: '',
            country: '',
            message: '',
            consent: false,
            marketingConsent: false
          });
          onClose();
        }, 2000);
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrors({ submit: 'Failed to send confirmation email. Please try again or contact us directly.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name.trim() &&
                     formData.phone.trim() &&
                     formData.email.trim() &&
                     formData.country.trim() &&
                     formData.consent;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[20px] w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Contact Sales Team</h3>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              aria-label="Close form"
            >
              <span className="text-gray-600 font-bold">✕</span>
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Get a personalized quote for your {formatPrice(totalPrice)} build configuration
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-4 space-y-4" noValidate>
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                maxLength="100"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your full name"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
              />
              {errors.name && (
                <p id="name-error" className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                maxLength="20"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your phone number"
                aria-invalid={!!errors.phone}
                aria-describedby={errors.phone ? 'phone-error' : undefined}
              />
              {errors.phone && (
                <p id="phone-error" className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                maxLength="100"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your email address"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && (
                <p id="email-error" className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Country Field */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                Country *
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.country ? 'border-red-500' : 'border-gray-300'
                }`}
                aria-invalid={!!errors.country}
                aria-describedby={errors.country ? 'country-error' : undefined}
              >
                <option value="">Select your country</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="Australia">Australia</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="Netherlands">Netherlands</option>
                <option value="Belgium">Belgium</option>
                <option value="Switzerland">Switzerland</option>
                <option value="Austria">Austria</option>
                <option value="Italy">Italy</option>
                <option value="Spain">Spain</option>
                <option value="Portugal">Portugal</option>
                <option value="Ireland">Ireland</option>
                <option value="Denmark">Denmark</option>
                <option value="Sweden">Sweden</option>
                <option value="Norway">Norway</option>
                <option value="Finland">Finland</option>
                <option value="Japan">Japan</option>
                <option value="South Korea">South Korea</option>
                <option value="China">China</option>
                <option value="India">India</option>
                <option value="United Arab Emirates">United Arab Emirates</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Qatar">Qatar</option>
                <option value="Bahrain">Bahrain</option>
                <option value="Kuwait">Kuwait</option>
                <option value="Singapore">Singapore</option>
                <option value="Malaysia">Malaysia</option>
                <option value="Thailand">Thailand</option>
                <option value="Brazil">Brazil</option>
                <option value="Mexico">Mexico</option>
                <option value="South Africa">South Africa</option>
                <option value="New Zealand">New Zealand</option>
                <option value="Poland">Poland</option>
                <option value="Czech Republic">Czech Republic</option>
                <option value="Luxembourg">Luxembourg</option>
                <option value="Monaco">Monaco</option>
                <option value="Other">Other</option>
              </select>
              {errors.country && (
                <p id="country-error" className="text-red-500 text-xs mt-1">{errors.country}</p>
              )}
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Message (Optional)
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={3}
                maxLength="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Any specific questions or requirements?"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.message.length}/1000 characters
              </p>
            </div>

            {/* Consent Checkboxes */}
            <div className="space-y-3">
              <div>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                    aria-describedby="consent-error"
                  />
                  <span className="text-sm text-gray-700">
                    I agree to the{' '}
                    <a 
                      href="/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Privacy Policy
                    </a>
                    {' '}and consent to being contacted regarding this inquiry *
                  </span>
                </label>
                {errors.consent && (
                  <p id="consent-error" className="text-red-500 text-xs mt-1 ml-6">
                    {errors.consent}
                  </p>
                )}
              </div>

              <div>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    name="marketingConsent"
                    checked={formData.marketingConsent}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    I would like to receive marketing communications about products and offers
                  </span>
                </label>
              </div>
            </div>

            {/* Build Summary */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm font-medium text-gray-700 mb-2">Your Build Summary:</div>
              <div className="text-xs text-gray-600 space-y-1">
                <div>• {Object.keys(selectedProducts).length} components selected</div>
                <div>• Total value: {formatPrice(totalPrice)}</div>
                <div>• Confirmation email will be sent to you</div>
              </div>
              <p className="text-xs text-gray-500 mt-2 italic">
                Prices shown exclude taxes, shipping and import duties. These will be confirmed in your personalized quote.
              </p>
            </div>

            {/* Error Messages */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <span className="text-red-600">⚠</span>
                  <div className="text-sm text-red-800">{errors.submit}</div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <div className="text-sm text-green-800">
                    <div className="font-medium">Email sent successfully!</div>
                    <div className="text-xs">Check your inbox for confirmation.</div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Form Actions */}
        <div className="p-4 border-t space-y-2">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !isFormValid}
            className={`w-full py-3 rounded-[15px] font-medium transition-all duration-200 ${
              isSubmitting || !isFormValid
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white active:scale-[0.98]'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </div>
            ) : (
              '📧 Send Inquiry & Get Confirmation'
            )}
          </button>
          <button
            onClick={onClose}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-[15px] font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailSalesForm;
